import { CheckInDetail, CheckInSearch, Service } from '../interfaces/check_in';
import { CheckInsPerPetSearch, PetCheckInsDetail, RegistrationsPerMonthDetail, RegistrationsPerMonthSearch } from '../interfaces/report';
import { CheckOutAvailableItem, CheckOutDetail, CheckOutSearch } from '../interfaces/check_out';
import { Disease, PetDetail, PetSearch, PetType, Vaccine } from '../interfaces/pet';
import { ExpenseAvailableItem, ExpenseDetail, ExpenseSearch } from '../interfaces/expense';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { InvoiceDetail, InvoiceSearch } from '../interfaces/invoice'
import { OwnerDetail, OwnerSearch, OwnerType } from '../interfaces/owner';
import { PickUpDetail, PickUpSearch } from '../interfaces/pick-up';
import { UserDetail, UserSearch } from '../interfaces/user';

import { BranchDetail } from '../interfaces/branch';
import { Injectable } from '@angular/core';
import { LoginToken } from '../interfaces/auth';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment.prod';
import { QRCodeDetail, QRCodeSearch } from '../interfaces/qr-code';
import { Backup } from '../interfaces/database_backup';

@Injectable()
export class PetService {
  private url = environment.apiBackendUrl;
  private getTokenUrl = `${this.url}/auth/token`;
  private revokeTokenUrl = `${this.url}/auth/revoke`;
  private apiUrl = `${this.url}/api`;
  private branchUrl = `${this.apiUrl}/branch`;
  private petUrl = `${this.apiUrl}/pet`;
  private petSearchUrl = `${this.petUrl}/search`;
  private petRegisterUrl = `${this.petUrl}/register`;
  private petTypeUrl = `${this.petUrl}/type`;
  private ownerUrl = `${this.apiUrl}/owner/`;
  private ownerTypeUrl = `${this.apiUrl}/owner/type/`;
  private ownerSearchUrl = `${this.ownerUrl}search`;
  private expenseUrl = `${this.apiUrl}/expense`;
  private expenseSearchUrl = `${this.expenseUrl}/search`;
  private expenseAvailableItemUrl = `${this.expenseUrl}/available-item`;
  private diseaseUrl = `${this.apiUrl}/disease/`;
  private vaccineUrl = `${this.apiUrl}/vaccine/`;
  private serviceUrl = `${this.apiUrl}/service/`;
  private checkInUrl = `${this.apiUrl}/check-in/`;
  private checkInSearchUrl = `${this.checkInUrl}search`;
  private checkOutUrl = `${this.apiUrl}/check-out/`;
  private checkOutAvailableItemsUrl = `${this.apiUrl}/check-out/available-items`;
  private checkOutSearchUrl = `${this.checkOutUrl}search`;
  private invoiceUrl = `${this.apiUrl}/invoice`;
  private invoiceSearchUrl = `${this.invoiceUrl}/search`;
  private reportUrl = `${this.apiUrl}/report`;
  private reportPdfUrl = `${this.reportUrl}/pdf`;
  private reportPdfInvoiceUrl = `${this.reportPdfUrl}/invoice`;
  private reportBase64PdfInvoiceUrl = `${this.reportPdfUrl}/base64-invoice`;
  private reportCheckInsPerPetUrl = `${this.reportUrl}/check-ins-per-pet`;
  private reportCheckInsPerPetSearchUrl = `${this.reportCheckInsPerPetUrl}/search`;
  private reportRegistrationsPerMonthUrl = `${this.reportUrl}/registrations-per-month`;
  private reportRegistrationsPerMonthSearchUrl = `${this.reportRegistrationsPerMonthUrl}/search`;
  private reportDashboardUrl = `${this.reportUrl}/dashboard`;
  private reportMonthlyNetIncomeUrl = `${this.reportUrl}/monthly-net-income`;
  private reportMonthlyRevenuePerItemUrl = `${this.reportUrl}/monthly-revenue-per-item`;
  private reportMonthlyExpensesPerItemUrl = `${this.reportUrl}/monthly-expenses-per-item`;
  private pickUpUrl = `${this.apiUrl}/pick-up`;
  private pickUpSearchUrl = `${this.pickUpUrl}/search`;
  private pickUpBookingUrl = `${this.pickUpUrl}/book`;
  private userUrl = `${this.apiUrl}/user`;
  private userSearchUrl = `${this.userUrl}/search`;
  private userSignupUrl = `${this.userUrl}/signup`;
  private userPermissionUrl = `${this.userUrl}/permission`;
  private userCurrentUserUrl = `${this.userUrl}/current`;
  private exportUrl = `${this.apiUrl}/export`;
  private exportMonthlyNetIncomeUrl = `${this.exportUrl}/monthly-net-income`;
  private exportExcelMonthlyNetIncomeUrl = `${this.exportMonthlyNetIncomeUrl}/excel`;
  private exportPetsUrl = `${this.exportUrl}/pets`;
  private exportPetsToExcelUrl = `${this.exportPetsUrl}/excel`;
  private exportOwnersUrl = `${this.exportUrl}/owners`;
  private exportOwnersToExcelUrl = `${this.exportOwnersUrl}/excel`;
  private exportMonthlyRevenuesPerItemUrl = `${this.exportUrl}/monthly-revenues-per-item`;
  private exportMonthlyRevenuesPerItemToExcelUrl = `${this.exportMonthlyRevenuesPerItemUrl}/excel`;
  private exportMonthlyExpensesPerItemUrl = `${this.exportUrl}/monthly-expenses-per-item`;
  private exportMonthlyExpensesPerItemToExcelUrl = `${this.exportMonthlyExpensesPerItemUrl}/excel`;
  private permissionUrl = `${this.apiUrl}/permission`;
  private qrCodeUrl = `${this.apiUrl}/qr-code/`;
  private qrCodeSearchUrl = `${this.qrCodeUrl}search`;
  private qrCodePublicUrl = `${this.qrCodeUrl}public`;
  private backupUrl = `${this.apiUrl}/backup/`;
  private backupDownloadUrl = `${this.apiUrl}/backup/download`;
  

  private permissionsSubject = new BehaviorSubject<string[]>([]); // Store permissions
  permissions$ = this.permissionsSubject.asObservable(); // Expose as Observable

  private currentUserSubject = new BehaviorSubject<any>(null); // Store permissions
  currentUser$ = this.currentUserSubject.asObservable(); // Expose as Observable

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
  ) { }

  private generateHeader() {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set(
      'Authorization',
      'Bearer ' + localStorage.getItem('access_token')
    );
    return headers;
  }

  getApiUrl()
  {
    return this.apiUrl;
  }

  //
  // Start of Authentication section
  //
  login(username: string, password: string): Observable<any> {
    return this.http.post<LoginToken>(this.getTokenUrl, {
      username: username,
      password: password,
    });
  }

  isTokenExpired() {
    const token_expiration = localStorage.getItem('token_expiration') || '0';
    const expiration = Number.parseInt(
      token_expiration,
      10
    );
    const now = new Date().getTime();
    return now >= expiration;
  }

  isLoggedIn() {
    if (localStorage.getItem('access_token')) {
      return true;
    }
    return false;
  }

   /** Check if user is authenticated (has a token) */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    return !!token;
  }

  logout(token: string) {
    localStorage.removeItem("permissions");
    this.permissionsSubject.next([]); // Clear permissions
    return this.http.post(this.revokeTokenUrl, { token: token });
  }
  // End of Authentication section

  //
  // Start of Branch section
  //

  getBranches(): Observable<BranchDetail[]>  {
    return this.http.get<BranchDetail[]>(
      this.branchUrl,
      {
        headers: this.generateHeader(),
      }
    );
  }

  // End of Branch section

  //
  // Start of Pets section
  //

  getPets(): Observable<any[]>  {
    return this.http.get<any[]>(
      this.petUrl,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchPets(limit: number, offset: number, name: string, startDate: string, endDate: string): Observable<PetSearch>  {
    const url = `${this.petSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get<PetSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getPet(id: string): Observable<PetDetail>  {
    return this.http.get<PetDetail>(
      `${this.petUrl}/${id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updatePet(pet_id: string, payload: any): Observable<PetDetail>  {
    return this.http.put<PetDetail>(
      `${this.petUrl}/${pet_id}/`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createPet(payload: any): Observable<PetDetail>  {
    return this.http.post<PetDetail>(
      `${this.petUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  registerPet(payload: any): Observable<PetDetail>  {
    return this.http.post<PetDetail>(
      `${this.petRegisterUrl}`,
      payload
    );
  }

  getPetTypes(): Observable<PetType[]>  {
    return this.http.get<PetType[]>(
      `${this.petTypeUrl}`
    );
  }

  uploadPetImage(petId: string, payload: any): Observable<any>  {
    return this.http.post<any>(
      `${this.petUrl}/${petId}/image`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  // End of Pet section

  //
  // Start of Owners section
  //

  getOwners(): Observable<OwnerDetail[]>  {
    return this.http.get<OwnerDetail[]>(
      `${this.ownerUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchOwners(limit: number, offset: number, name: string, type: string, startDate: string, endDate: string): Observable<OwnerSearch>  {
    const url = `${this.ownerSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&type=${type}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get<OwnerSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getOwner(id: string): Observable<OwnerDetail>  {
    return this.http.get<OwnerDetail>(
      `${this.ownerUrl}/${id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updateOwner(owner_id: string, payload: any): Observable<OwnerDetail>  {
    return this.http.put<OwnerDetail>(
      `${this.ownerUrl}${owner_id}/`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createOwner(payload: any): Observable<OwnerDetail>  {
    return this.http.post<OwnerDetail>(
      `${this.ownerUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getOwnerTypes(): Observable<OwnerType[]>  {
    return this.http.get<OwnerType[]>(
      `${this.ownerTypeUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }
  // End of Owners section

  //
  // Start of Expense section
  //

  getExpenses(): Observable<ExpenseDetail[]>  {
    return this.http.get<ExpenseDetail[]>(
      `${this.expenseUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchExpenses(limit: number, offset: number, item: string, startDate: string, endDate: string): Observable<ExpenseSearch>  {
    const url = `${this.expenseSearchUrl}?limit=${limit}&offset=${offset*limit}&item=${item}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get<ExpenseSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getExpense(id: string): Observable<ExpenseDetail>  {
    return this.http.get<ExpenseDetail>(
      `${this.expenseUrl}/${id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updateExpense(expense_id: string, payload: any): Observable<ExpenseDetail>  {
    return this.http.put<ExpenseDetail>(
      `${this.expenseUrl}/${expense_id}/`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createExpense(payload: any): Observable<ExpenseDetail>  {
    return this.http.post<ExpenseDetail>(
      `${this.expenseUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getExpenseAvailableItems(): Observable<ExpenseAvailableItem[]>  {
    return this.http.get<ExpenseAvailableItem[]>(
      `${this.expenseAvailableItemUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  // End of Owners section

  //
  // Start of Vaccines section
  //

  getVaccines(): Observable<Vaccine[]>  {
    return this.http.get<Vaccine[]>(
      `${this.vaccineUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createVaccine(payload: any): Observable<Vaccine> {
    return this.http.post<Vaccine>(
      `${this.vaccineUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    )
  }

  // End of Vaccines section

  //
  // Start of Diseases section
  //

  getDiseases(): Observable<Disease[]>  {
    return this.http.get<Disease[]>(
      `${this.diseaseUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }
  createDisease(payload: any): Observable<Disease> {
    return this.http.post<Disease>(
      `${this.diseaseUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    )
  }
  // End of Diseases section

  //
  // Start of service section
  //

  getServices(): Observable<Service[]>  {
    return this.http.get<Service[]>(
      `${this.serviceUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getService(service_id: string): Observable<Service>  {
    return this.http.get<Service>(
      `${this.serviceUrl}/${service_id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updateService(service_id: string, payload: any): Observable<Service>  {
    return this.http.put<Service>(
      `${this.ownerUrl}${service_id}/`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createService(payload: any): Observable<Service>  {
    return this.http.post<Service>(
      `${this.serviceUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }
  // End of *** section

  //
  // Start of check in section
  //

  getCheckIns(): Observable<CheckInDetail[]>  {
    return this.http.get<CheckInDetail[]>(
      `${this.checkInUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchCheckIns(limit: number, offset: number, name: string, code: string, startDate: string, endDate: string): Observable<CheckInSearch>  {
    const url = `${this.checkInSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&code=${code}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get<CheckInSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getCheckIn(check_in_id: string): Observable<CheckInDetail>  {
    return this.http.get<CheckInDetail>(
      `${this.checkInUrl}/${check_in_id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updateCheckIn(check_in_id: string, payload: any): Observable<CheckInDetail>  {
    return this.http.put<CheckInDetail>(
      `${this.checkInUrl}${check_in_id}/`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createCheckIn(payload: any): Observable<CheckInDetail>  {
    return this.http.post<CheckInDetail>(
      `${this.checkInUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }
  // End of check in section

  // #region CheckOut
  getCheckOuts(): Observable<CheckOutDetail[]>  {
    return this.http.get<CheckOutDetail[]>(
      `${this.checkOutUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchCheckOuts(limit: number, offset: number, name: string, code: string,
    checkInStartDate: string, checkInEndDate: string,
    checkOutStartDate: string, checkOutEndDate: string): Observable<CheckOutSearch>  {
    const url = `${this.checkOutSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&code=${code}` +
      `&check_in_start_date=${checkInStartDate}&check_in_end_date=${checkInEndDate}&check_out_start_date=${checkOutStartDate}&check_out_end_date=${checkOutEndDate}`
    return this.http.get<CheckOutSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getCheckOut(check_out_id: string): Observable<CheckOutDetail>  {
    return this.http.get<CheckOutDetail>(
      `${this.checkOutUrl}/${check_out_id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updateCheckOut(check_out_id: string, payload: any): Observable<CheckOutDetail>  {
    return this.http.put<CheckOutDetail>(
      `${this.checkOutUrl}${check_out_id}/`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createCheckOut(payload: any): Observable<CheckOutDetail>  {
    return this.http.post<CheckOutDetail>(
      `${this.checkOutUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getCheckOutAvailableItems(): Observable<CheckOutAvailableItem[]>  {
    return this.http.get<CheckOutAvailableItem[]>(
      `${this.checkOutAvailableItemsUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getCheckOutInvoice(check_out_id: string): Observable<InvoiceDetail>  {
    return this.http.get<InvoiceDetail>(
      `${this.checkOutUrl}${check_out_id}/invoice`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  // #endregion

  // #region Invoice
  getInvoices(): Observable<InvoiceDetail[]> {
    return this.http.get<InvoiceDetail[]>(
      `${this.invoiceUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchInvoices(limit: number, offset: number, name: string, code: string,
    startDate: string, endDate: string,
    checkInStartDate: string, checkInEndDate: string,
    checkOutStartDate: string, checkOutEndDate: string): Observable<InvoiceSearch>  {
    const url = `${this.invoiceSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&code=${code}` +
      `&start_date=${startDate}&end_date=${endDate}` +
      `&check_in_start_date=${checkInStartDate}&check_in_end_date=${checkInEndDate}` +
      `&check_out_start_date=${checkOutStartDate}&check_out_end_date=${checkOutEndDate}`

    return this.http.get<InvoiceSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getInvoice(invoice_id: string): Observable<InvoiceDetail> {
    return this.http.get<InvoiceDetail>(
      `${this.invoiceUrl}/${invoice_id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createInvoice(payload: any): Observable<InvoiceDetail> {
    return this.http.post<InvoiceDetail>(
      `${this.invoiceUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  // #endregion

  //
  // Start of PDF section
  //

  // #region Invoice

  getPdfInvoice(id: string): Observable<Blob> {
    const url = `${this.reportPdfInvoiceUrl}/${id}`;
    return this.http.get<Blob>(
      url,
      {
        headers: this.generateHeader(),
        // responseType: 'arraybuffer',
      }
    );
  }

  getPdfBase64Invoice(id: string) {
    const url = `${this.reportBase64PdfInvoiceUrl}/${id}`;
    return this.http.get<any>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }



  // #endregion

  // End of PDF section

  //
  // Start of report section
  //

  // #region Report

  // Search check ins per pet
  searchCheckInsPerPet(limit: number, offset: number, name: string, code: string,
    startDate: string, endDate: string,
    checkInStartDate: string, checkInEndDate: string,
    checkOutStartDate: string, checkOutEndDate: string): Observable<CheckInsPerPetSearch>  {
    const url = `${this.reportCheckInsPerPetSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&code=${code}` +
      `&start_date=${startDate}&end_date=${endDate}` +
      `&check_in_start_date=${checkInStartDate}&check_in_end_date=${checkInEndDate}` +
      `&check_out_start_date=${checkOutStartDate}&check_out_end_date=${checkOutEndDate}`

    return this.http.get<CheckInsPerPetSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  } // End of search check ins per pet

  // Get pet check ins detail
  getPetCheckInsDetail(id: string): Observable<PetCheckInsDetail>  {
    const url = `${this.reportCheckInsPerPetUrl}/${id}`

    return this.http.get<PetCheckInsDetail>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  } // End of get pet check ins detail

  // Search registrations per month
  searchRegistrationsPerMonth(limit: number, offset: number, year: string, month: string,): Observable<RegistrationsPerMonthSearch>  {
    const url = `${this.reportRegistrationsPerMonthSearchUrl}?limit=${limit}&offset=${offset*limit}&year=${year}&month=${month}`

    return this.http.get<RegistrationsPerMonthSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  } // End of search registrations per month

  // Get registrations per month
  getRegistrationsPerMonth(): Observable<RegistrationsPerMonthDetail[]>  {
    // const url = `${this.reportRegistrationsPerMonthUrl}?limit=${limit}&offset=${offset*limit}&year=${year}&month=${month}`
    return this.http.get<RegistrationsPerMonthDetail[]>(
      this.reportRegistrationsPerMonthUrl,
      {
        headers: this.generateHeader(),
      }
    );
  } // End of get registrations per month

  // Get summarized reports for dashboard
  getDashboardReports(): Observable<any>  {
    return this.http.get<any>(
      this.reportDashboardUrl,
      {
        headers: this.generateHeader(),
      }
    );
  } // End of get summarized reports for dashboard

  // Get monthly net income
  getMonthlyNetIncome(): Observable<any>  {
    return this.http.get<any>(
      this.reportMonthlyNetIncomeUrl,
      {
        headers: this.generateHeader(),
      }
    );
  }// End of get monthly net income

  // Export monthly net income
  exportMonthlyNetIncome(): Observable<ArrayBuffer>  {
    return this.http.get(
      this.exportExcelMonthlyNetIncomeUrl,
      {
        headers: this.generateHeader(),
        responseType: 'arraybuffer',
      }
    );
  } // End of export monthly net income

  // Export pets to Excel
  exportPetsToExcel(name: string, startDate: string, endDate: string): Observable<ArrayBuffer>  {
    const url = `${this.exportPetsToExcelUrl}?name=${name}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get(
      url,
      {
        headers: this.generateHeader(),
        responseType: 'arraybuffer',
      }
    );
  } // End of export pets to Excel

  // Export owners to Excel
  exportOwnersToExcel(name: string, type: string, startDate: string, endDate: string): Observable<ArrayBuffer>  {
    const url = `${this.exportOwnersToExcelUrl}?name=${name}&type=${type}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get(
      url,
      {
        headers: this.generateHeader(),
        responseType: 'arraybuffer',
      }
    );
  } // End of export owners to Excel

  // Get monthly revenue per item
  getMonthlyRevenuePerItem(): Observable<any>  {
    return this.http.get<any>(
      this.reportMonthlyRevenuePerItemUrl,
      {
        headers: this.generateHeader(),
      }
    );
  }// End of get monthly revenue per item

  // Get monthly expenses per item
  getMonthlyExpensesPerItem(): Observable<any>  {
    return this.http.get<any>(
      this.reportMonthlyExpensesPerItemUrl,
      {
        headers: this.generateHeader(),
      }
    );
  }// End of get monthly expenses per item

  // Export monthly revenues per item to Excel
  exportMonthlyRevenuesPerItemToExcel(): Observable<ArrayBuffer>  {
    return this.http.get(
      this.exportMonthlyRevenuesPerItemToExcelUrl,
      {
        headers: this.generateHeader(),
        responseType: 'arraybuffer',
      }
    );
  } // End of export monthly revenues per item to Excel

  // Export monthly revenues per item to Excel
  exportMonthlyExpensesPerItemToExcel(): Observable<ArrayBuffer>  {
    return this.http.get(
      this.exportMonthlyExpensesPerItemToExcelUrl,
      {
        headers: this.generateHeader(),
        responseType: 'arraybuffer',
      }
    );
  } // End of exportMonthlyExpensesPerItemToExcel

  // #endregion


  // End of report section

  //
  // Start of Pickups section
  //

  getPickUps(): Observable<PickUpDetail[]>  {
    return this.http.get<PickUpDetail[]>(
      `${this.pickUpUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchPickUps(limit: number, offset: number, name: string, phone: string, startDate: string, endDate: string): Observable<PickUpSearch>  {
    const url = `${this.pickUpSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&phone=${phone}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get<PickUpSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getPickUp(id: string): Observable<PickUpDetail>  {
    return this.http.get<PickUpDetail>(
      `${this.pickUpUrl}/${id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updatePickUp(pick_up_id: string, payload: any): Observable<PickUpDetail>  {
    return this.http.put<PickUpDetail>(
      `${this.pickUpUrl}/${pick_up_id}/`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createPickUp(payload: any): Observable<PickUpDetail>  {
    return this.http.post<PickUpDetail>(
      `${this.pickUpUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  bookPickUp(payload: any): Observable<PickUpDetail>  {
    return this.http.post<PickUpDetail>(
      `${this.pickUpBookingUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  // End of Pick up section

  //
  // Start of Owners section
  //

  getUsers(): Observable<UserDetail[]>  {
    return this.http.get<UserDetail[]>(
      `${this.userUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  searchUsers(limit: number, offset: number, username: string, name: string): Observable<UserSearch>  {
    const url = `${this.userSearchUrl}?limit=${limit}&offset=${offset*limit}&username=${username}&name=${name}`
    return this.http.get<UserSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getUser(id: string): Observable<UserDetail>  {
    return this.http.get<UserDetail>(
      `${this.ownerUrl}/${id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  updateUser(user_id: string, payload: any): Observable<UserDetail>  {
    return this.http.put<UserDetail>(
      `${this.userUrl}/${user_id}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createUser(payload: any): Observable<UserDetail>  {
    return this.http.post<UserDetail>(
      `${this.userUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  signupUser(payload: any): Observable<UserDetail>  {
    return this.http.post<UserDetail>(
      `${this.userSignupUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getCurrentUserDetail(): Observable<UserDetail>  {
    return this.http.get<UserDetail>(
      `${this.userCurrentUserUrl}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  setCurrentUser(user: any) {
    console.log(`setCurrentUser, user : ${JSON.stringify(user)}`);
    this.currentUserSubject.next(user);
  }

  // Method to get the current user value
  getCurrentUser() {
    return this.currentUserSubject.value;
  }

  // End of Owners section


  //
  // Start of Permission section
  //

   /** ✅ Fetch user permissions from API */
  // loadPermissions() {
  //   this.http.get<{ permissions: string[] }>(this.permissionUrl).subscribe(
  //     (res) => {
  //       this.permissions.next(res.permissions || []);
  //     },
  //     (err) => {
  //       console.error("Error loading permissions:", err);
  //     }
  //   );
  // }

   /** ✅ Fetch permissions ONCE and store in BehaviorSubject */
  // loadPermissions(): Observable<string[]> {
  //   return this.http.get<string[]>(`${this.permissionUrl}`,
  //     {
  //       headers: this.generateHeader(),
  //     }
  //   ).pipe(
  //     tap((permissions) => {
  //       console.log(`loadPermissions: ${JSON.stringify(permissions)}`);
  //       this.permissionsSubject.next(permissions); // Store globally
  //       localStorage.setItem("permissions", JSON.stringify(permissions)); // Optional caching
  //     })
  //   );
  // }

  // Get permissions
  loadPermissions(): Observable<any>  {
    return this.http.get<any>(
      this.permissionUrl,
      {
        headers: this.generateHeader(),
      }
    );
  }// End of get permissions

  // Get user permissions
  loadUserPermissions(): Observable<any>  {
    return this.http.get<any>(
      this.userPermissionUrl,
      {
        headers: this.generateHeader(),
      }
    );
  }// End of get user permissions

  /** ✅ Get current permissions (sync) */
  getPermissions(): string[] {
    return this.permissionsSubject.getValue();
  }

  setPermissions(permissions: string[]) {
    console.log(`setPermissions, permissions : ${JSON.stringify(permissions)}`);
    this.permissionsSubject.next(permissions);
  }

  /** ✅ Check if user has a specific permission */
  hasPermission(permission: string): boolean {
    console.log(`hasPermission, permission: ${JSON.stringify(permission)}`);
    return this.getPermissions().includes(permission);
  }

  /** ✅ Check if user has a specific permission */
  // hasPermission(permission: string): boolean {
  //   return this.permissions.getValue().includes(permission);
  // }

  // End of Permission section


  //
  // Start of QR Code section
  //
  // #region QR Code

  searchQRCodes(limit: number, offset: number, name: string, startDate: string, endDate: string): Observable<QRCodeSearch>  {
    const url = `${this.qrCodeSearchUrl}?limit=${limit}&offset=${offset*limit}&name=${name}&start_date=${startDate}&end_date=${endDate}`
    return this.http.get<QRCodeSearch>(
      url,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getQRCode(id: string): Observable<QRCodeDetail>  {
    return this.http.get<QRCodeDetail>(
      `${this.qrCodeUrl}/${id}`,
      {
        headers: this.generateHeader(),
      }
    );
  }

  getQRCodePublic(id: string): Observable<any>  {
    return this.http.get<any>(
      `${this.qrCodePublicUrl}/${id}`,
    );
  }

  updateQRCode(id: string, payload: any): Observable<QRCodeDetail>  {
    return this.http.put<QRCodeDetail>(
      `${this.qrCodeUrl}${id}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }

  createQRCode(payload: any): Observable<QRCodeDetail>  {
    return this.http.post<QRCodeDetail>(
      `${this.qrCodeUrl}`,
      payload,
      {
        headers: this.generateHeader(),
      }
    );
  }


  // #endregion
  // End of *** section

  //
  // Start of Database Backup section
  //

  createDatabaseBackup(format: string): Observable<any> {
    return this.http.post(
      this.backupUrl,
      {format},
      {
        headers: this.generateHeader(),
      });
  }

  getDatabaseBackups(): Observable<Backup[]> {
    return this.http.get<Backup[]>(
      this.backupUrl,
      {
        headers: this.generateHeader(),
      });
  }

  downloadDatabaseBackup(filename: string): Observable<ArrayBuffer> {
    return this.http.get(
      `${this.backupDownloadUrl}/${filename}`,
      {
      headers: this.generateHeader(),
      // responseType: 'blob',
      responseType: 'arraybuffer',
      // headers: this.generateHeader(),
    });
  }

  // exportMonthlyExpensesPerItemToExcel(): Observable<ArrayBuffer>  {
  //   return this.http.get(
  //     this.exportMonthlyExpensesPerItemToExcelUrl,
  //     {
  //       headers: this.generateHeader(),
  //       responseType: 'arraybuffer',
  //     }
  //   );
  // } // End of exportMonthlyExpensesPerItemToExcel

  // End of Database Backup section

  //
  // Start of *** section
  //


  // End of *** section

  //
  // Start of *** section
  //


  // End of *** section

  //
  // Start of *** section
  //


  // End of *** section

}
